import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ComplianceFooter from '../components/ComplianceFooter';
import axios from 'axios';
import { LogOut, Users, FileText, TrendingUp, CheckCircle, XCircle, Mail, Download, Sparkles, BarChart3, Gift, MoreHorizontal, ShieldOff, Award, RefreshCw, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel } from '../components/ui/dropdown-menu';
import AdminManualPaymentsTab from '../components/AdminManualPaymentsTab';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [analytics, setAnalytics] = useState({});
  const [users, setUsers] = useState([]);
  const [tenders, setTenders] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [foundingOffer, setFoundingOffer] = useState({ buyer: {}, supplier: {} });
  const [foundingBreakdown, setFoundingBreakdown] = useState({ buyer: {}, supplier: {}, totals: {} });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('users');
  // Feb 2026 — Super-Admin-only toggle to bypass the production-clean filter.
  const [includeTest, setIncludeTest] = useState(() => localStorage.getItem('admin_include_test') === '1');

  // Grant Membership modal state
  const [grantModal, setGrantModal] = useState(null);
  const [grantForm, setGrantForm] = useState({
    plan_key: 'buyer_yearly',
    is_complimentary: true,
    discount_percent: 0,
    duration_days: 365,
    end_date_override: '',
    notes: '',
    mark_as_founding: false,
  });

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  useEffect(() => {
    fetchData();
    // Poll every 20s so newly-registered pending users + approvals show up
    // in near-real-time without a manual refresh.
    const iv = setInterval(fetchData, 20000);
    return () => clearInterval(iv);
  }, [includeTest]);

  const fetchData = async () => {
    try {
      const p = { include_test: includeTest ? 1 : 0 };
      const [analyticsRes, usersRes, tendersRes, inquiriesRes, offerRes, breakdownRes] = await Promise.all([
        axios.get(`${API}/admin/analytics`,           { withCredentials: true, params: p }),
        axios.get(`${API}/admin/users`,               { withCredentials: true, params: p }),
        axios.get(`${API}/admin/tenders`,             { withCredentials: true, params: p }),
        axios.get(`${API}/admin/inquiries`,           { withCredentials: true }),
        axios.get(`${API}/founding-offer`),
        axios.get(`${API}/admin/founding/breakdown`,  { withCredentials: true })
      ]);
      setAnalytics(analyticsRes.data);
      setUsers(usersRes.data?.items || usersRes.data || []);
      setTenders(tendersRes.data);
      setInquiries(inquiriesRes.data);
      setFoundingOffer(offerRes.data);
      setFoundingBreakdown(breakdownRes.data);
    } catch (error) {
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveUser = async (userId, approved) => {
    try {
      await axios.patch(`${API}/admin/users/approve`, { user_id: userId, approved }, { withCredentials: true });
      toast.success(approved ? 'User approved successfully' : 'Approval revoked successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to update user status');
    }
  };

  const handleRejectUser = async (userId, displayName) => {
    if (!window.confirm(`Reject registration for "${displayName}"?\n\nThis removes the temporary registration data and frees the email for fresh signup. An immutable audit log entry is retained for compliance.`)) return;
    try {
      await axios.post(`${API}/admin/registrations/${userId}/reject`, {}, { withCredentials: true });
      toast.success('Registration rejected. Email is now available for fresh signup.');
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to reject registration');
    }
  };

  const handleDeleteRejected = async (tombstoneId, displayName) => {
    if (!window.confirm(`Permanently remove rejected registration for "${displayName}" from the queue?\n\nAudit log entries will be retained for compliance.`)) return;
    try {
      await axios.delete(`${API}/admin/registrations/rejected/${tombstoneId}`, { withCredentials: true });
      toast.success('Rejected registration removed from queue.');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete rejected registration');
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await axios.get(`${API}/admin/export/users`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'users_export.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Users exported successfully');
    } catch (error) {
      toast.error('Failed to export users');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const openGrantModal = (u) => {
    setGrantForm({
      plan_key: u.role === 'supplier' ? 'supplier_yearly' : 'buyer_yearly',
      is_complimentary: true,
      discount_percent: 0,
      duration_days: 365,
      end_date_override: '',
      notes: '',
      mark_as_founding: false,
    });
    setGrantModal(u);
  };

  const submitGrant = async (e) => {
    e.preventDefault();
    try {
      const payload = { user_id: grantModal.id, ...grantForm };
      if (!payload.end_date_override) delete payload.end_date_override;
      await axios.post(`${API}/admin/memberships/grant`, payload, { withCredentials: true });
      toast.success(`Membership granted to ${grantModal.name}`);
      setGrantModal(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to grant');
    }
  };

  const markFounding = async (userId, name) => {
    if (!window.confirm(`Mark "${name}" as Founding Member (24 months free)?`)) return;
    try {
      await axios.post(`${API}/admin/memberships/mark-founding/${userId}`, {}, { withCredentials: true });
      toast.success('Marked as Founding Member');
      fetchData();
    } catch (err) {
      toast.error('Failed to mark founding');
    }
  };

  const removeFounding = async (userId, name) => {
    if (!window.confirm(`Remove Founding Member status from "${name}"?`)) return;
    try {
      await axios.post(`${API}/admin/memberships/remove-founding/${userId}`, {}, { withCredentials: true });
      toast.success('Founding status removed');
      fetchData();
    } catch (err) {
      toast.error('Failed to remove founding');
    }
  };

  const toggleSuspend = async (userId, name, currentlySuspended) => {
    const action = currentlySuspended ? 'unsuspend' : 'suspend';
    if (!window.confirm(`${action.charAt(0).toUpperCase() + action.slice(1)} "${name}"?`)) return;
    try {
      const r = await axios.post(`${API}/admin/memberships/suspend/${userId}`, {}, { withCredentials: true });
      toast.success(r.data.suspended ? 'User suspended' : 'User unsuspended');
      fetchData();
    } catch (err) {
      toast.error('Failed');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="glass border-b border-border/40 sticky top-0 z-50">
        <nav className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Sparkles className="h-6 w-6 text-gold" />
              <h1 className="text-2xl font-bold text-navy">Admin Dashboard</h1>
            </div>
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              <button onClick={() => navigate('/admin-reports')} className="px-2 sm:px-3 py-1.5 sm:py-2 rounded-md bg-gold text-navy font-semibold text-xs sm:text-sm inline-flex items-center" data-testid="nav-reports-btn">
                <BarChart3 className="h-4 w-4 sm:mr-2" /> <span className="hidden sm:inline">Reports & Memberships</span>
              </button>
              <button onClick={() => navigate('/admin/fy-drill')} className="px-2 sm:px-3 py-1.5 sm:py-2 rounded-md bg-orange text-white font-semibold text-xs sm:text-sm inline-flex items-center" data-testid="nav-fy-drill-btn">
                <RefreshCw className="h-4 w-4 sm:mr-2" /> <span className="hidden sm:inline">FY Closure Drill</span>
              </button>
              <button onClick={() => navigate('/admin/settings')} className="px-2 sm:px-3 py-1.5 sm:py-2 rounded-md border-2 border-navy text-navy font-semibold text-xs sm:text-sm inline-flex items-center hover:bg-navy hover:text-white transition-colors" data-testid="nav-platform-settings-btn">
                <span className="hidden sm:inline">Platform Settings</span><span className="sm:hidden">⚙</span>
              </button>
              <span className="hidden md:inline text-sm text-muted-foreground">{user?.email}</span>
              <button onClick={handleLogout} className="px-2 sm:px-4 py-1.5 sm:py-2 rounded-md border border-input text-xs sm:text-sm" data-testid="logout-btn">
                <LogOut className="inline h-4 w-4 sm:mr-2" /> <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </div>
        </nav>
      </header>

      {/* Production-data filter banner (Feb 2026 — Founder mandate) */}
      {includeTest && (
        <div className="bg-red-600 text-white py-2 px-6 text-sm font-bold flex items-center justify-between" data-testid="include-test-banner">
          <span>⚠ TEST-DATA MODE — you are viewing demo, QA, tombstoned and unapproved records. Do not use these numbers for reporting.</span>
          <button onClick={() => { setIncludeTest(false); localStorage.setItem('admin_include_test', '0'); }} className="underline">Hide test data</button>
        </div>
      )}
      <div className="container mx-auto px-6 pt-3 flex justify-end">
        <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer" data-testid="include-test-toggle-label">
          <input type="checkbox" checked={includeTest}
                 onChange={e => { setIncludeTest(e.target.checked); localStorage.setItem('admin_include_test', e.target.checked ? '1' : '0'); }}
                 data-testid="include-test-toggle" />
          <span>Include test / demo / rejected records <span className="text-slate-400">(Super-Admin only — production data hidden by default)</span></span>
        </label>
      </div>

      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="p-6 rounded-lg border border-border bg-card" data-testid="stat-total-users">
            <Users className="h-8 w-8 text-navy mb-2" />
            <div className="text-2xl font-bold">{analytics.total_users || 0}</div>
            <div className="text-sm text-muted-foreground">Total Users</div>
          </div>
          <div className="p-6 rounded-lg border border-border bg-card">
            <FileText className="h-8 w-8 text-gold mb-2" />
            <div className="text-2xl font-bold">{analytics.total_tenders || 0}</div>
            <div className="text-sm text-muted-foreground">Total Tenders</div>
          </div>
          <div className="p-6 rounded-lg border border-border bg-card">
            <TrendingUp className="h-8 w-8 text-orange mb-2" />
            <div className="text-2xl font-bold">{analytics.active_tenders || 0}</div>
            <div className="text-sm text-muted-foreground">Active Tenders</div>
          </div>
          <div className="p-6 rounded-lg border border-border bg-card" data-testid="stat-pending-approvals">
            <CheckCircle className="h-8 w-8 text-yellow-500 mb-2" />
            <div className="text-2xl font-bold">{analytics.pending_approvals || 0}</div>
            <div className="text-sm text-muted-foreground">Pending Approvals</div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="founding-breakdown">
          <div className="p-5 rounded-lg bg-navy text-white">
            <div className="text-xs uppercase tracking-wider text-gold mb-2">Public Buyer Founders</div>
            <div className="text-3xl font-bold">{foundingBreakdown.buyer?.public ?? 0}/100</div>
            <div className="text-xs text-white/70 mt-1">{foundingBreakdown.buyer?.remaining_public ?? 100} public slots left</div>
          </div>
          <div className="p-5 rounded-lg bg-navy text-white">
            <div className="text-xs uppercase tracking-wider text-orange mb-2">Public Supplier Founders</div>
            <div className="text-3xl font-bold">{foundingBreakdown.supplier?.public ?? 0}/100</div>
            <div className="text-xs text-white/70 mt-1">{foundingBreakdown.supplier?.remaining_public ?? 100} public slots left</div>
          </div>
          <div className="p-5 rounded-lg border-2 border-gold bg-gold/5">
            <div className="text-xs uppercase tracking-wider text-navy mb-2">Admin-Override Founders</div>
            <div className="text-3xl font-bold text-navy">{foundingBreakdown.totals?.admin_override ?? 0}</div>
            <div className="text-xs text-muted-foreground mt-1">B: {foundingBreakdown.buyer?.admin_override ?? 0} • S: {foundingBreakdown.supplier?.admin_override ?? 0}</div>
          </div>
          <div className="p-5 rounded-lg bg-card border border-border">
            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Total Founding Members</div>
            <div className="text-3xl font-bold text-navy">{foundingBreakdown.totals?.all_founding ?? 0}</div>
            <div className="text-xs text-muted-foreground mt-1">Offer: <span className={`font-semibold ${foundingBreakdown.public_offer_status === 'closed' ? 'text-red-600' : 'text-green-600'}`}>{(foundingBreakdown.public_offer_status || 'open').toUpperCase()}</span></div>
          </div>
        </div>

        <div className="flex space-x-2 mb-6 border-b border-border overflow-x-auto">
          <button onClick={() => setActiveTab('users')} className={`px-4 py-2 font-medium whitespace-nowrap ${activeTab === 'users' ? 'text-navy border-b-2 border-navy' : 'text-muted-foreground'}`} data-testid="tab-users">All Users</button>
          <button onClick={() => setActiveTab('buyers')} className={`px-4 py-2 font-medium whitespace-nowrap ${activeTab === 'buyers' ? 'text-navy border-b-2 border-navy' : 'text-muted-foreground'}`} data-testid="tab-buyers">Buyer Registrations</button>
          <button onClick={() => setActiveTab('suppliers')} className={`px-4 py-2 font-medium whitespace-nowrap ${activeTab === 'suppliers' ? 'text-navy border-b-2 border-navy' : 'text-muted-foreground'}`} data-testid="tab-suppliers">Supplier Registrations</button>
          <button onClick={() => setActiveTab('preregistrations')} className={`px-4 py-2 font-medium whitespace-nowrap ${activeTab === 'preregistrations' ? 'text-navy border-b-2 border-navy' : 'text-muted-foreground'}`} data-testid="tab-preregs">Pre-Registrations</button>
          <button onClick={() => setActiveTab('tenders')} className={`px-4 py-2 font-medium whitespace-nowrap ${activeTab === 'tenders' ? 'text-navy border-b-2 border-navy' : 'text-muted-foreground'}`} data-testid="tab-tenders">Tenders</button>
          <button onClick={() => setActiveTab('inquiries')} className={`px-4 py-2 font-medium whitespace-nowrap ${activeTab === 'inquiries' ? 'text-navy border-b-2 border-navy' : 'text-muted-foreground'}`} data-testid="tab-inquiries">All Inquiries</button>
          <button onClick={() => setActiveTab('manual_payments')} className={`px-4 py-2 font-medium whitespace-nowrap ${activeTab === 'manual_payments' ? 'text-navy border-b-2 border-navy' : 'text-muted-foreground'}`} data-testid="tab-manual-payments">Manual Payments</button>
        </div>

        {activeTab === 'users' && (
          <div className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">User Management</h2>
              <button onClick={handleExportCSV} className="px-4 py-2 rounded-md bg-gold text-navy font-semibold text-sm" data-testid="export-csv-btn">
                <Download className="inline h-4 w-4 mr-2" /> Export CSV
              </button>
            </div>
            {loading ? <div className="text-center py-8">Loading...</div> : users.length === 0 ? <div className="text-center py-8 text-muted-foreground">No users yet.</div> : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-3 px-4 font-medium">Name</th>
                      <th className="text-left py-3 px-4 font-medium">Email</th>
                      <th className="text-left py-3 px-4 font-medium">Role</th>
                      <th className="text-left py-3 px-4 font-medium">Status</th>
                      <th className="text-left py-3 px-4 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.id} className="border-b border-border hover:bg-muted/50" data-testid={`user-row-${u.id}`}>
                        <td className="py-3 px-4">{u.name}</td>
                        <td className="py-3 px-4">{u.email}</td>
                        <td className="py-3 px-4"><span className="px-2 py-1 rounded-full text-xs font-medium bg-navy/10 text-navy">{u.role.toUpperCase()}</span></td>
                        <td className="py-3 px-4">
                          <div className="flex flex-col gap-1">
                            <StatusBadge status={u.registration_status || (u.approved ? 'approved' : 'pending')} testid={`user-status-${u.id}`} />
                            {u.is_founding && <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-gold/30 text-navy w-fit" title={u.is_founding_admin_override ? 'Admin-marked' : 'Public Founder'}>★ {u.is_founding_admin_override ? 'FOUNDER (admin)' : 'FOUNDER'}</span>}
                            {u.suspended && <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-700 w-fit">SUSPENDED</span>}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          {u.role === 'admin' ? (
                            <span className="text-sm text-muted-foreground">-</span>
                          ) : u.is_rejected_tombstone ? (
                            <button
                              onClick={() => handleDeleteRejected(u.id, u.name || u.email)}
                              className="inline-flex items-center px-2.5 py-1 rounded-md border border-red-200 text-red-700 hover:bg-red-50 text-xs font-medium"
                              data-testid={`delete-rejected-${u.id}`}
                            >
                              <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Delete Registration
                            </button>
                          ) : (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <button className="p-1.5 rounded hover:bg-muted inline-flex items-center" data-testid={`actions-menu-${u.id}`}>
                                  <MoreHorizontal className="h-4 w-4" />
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-48 bg-white">
                                <DropdownMenuLabel className="text-xs text-muted-foreground">{u.name}</DropdownMenuLabel>
                                <DropdownMenuSeparator />
                                {!u.approved ? (
                                  <>
                                    <DropdownMenuItem onClick={() => handleApproveUser(u.id, true)} data-testid={`approve-user-${u.id}`}>
                                      <CheckCircle className="h-4 w-4 mr-2 text-green-600" /> Approve
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handleRejectUser(u.id, u.name || u.email)} data-testid={`reject-user-${u.id}`}>
                                      <XCircle className="h-4 w-4 mr-2 text-red-600" /> Reject
                                    </DropdownMenuItem>
                                  </>
                                ) : (
                                  <>
                                    {(u.role === 'buyer' || u.role === 'supplier') && (
                                      <>
                                        <DropdownMenuItem onClick={() => openGrantModal(u)} data-testid={`grant-membership-${u.id}`}>
                                          <Gift className="h-4 w-4 mr-2 text-navy" /> Grant Membership
                                        </DropdownMenuItem>
                                        {!u.is_founding ? (
                                          <DropdownMenuItem onClick={() => markFounding(u.id, u.name)} data-testid={`mark-founding-${u.id}`}>
                                            <Sparkles className="h-4 w-4 mr-2 text-gold" /> Mark Founding
                                          </DropdownMenuItem>
                                        ) : (
                                          <DropdownMenuItem onClick={() => removeFounding(u.id, u.name)} data-testid={`remove-founding-${u.id}`}>
                                            <Award className="h-4 w-4 mr-2 text-orange" /> Remove Founding
                                          </DropdownMenuItem>
                                        )}
                                      </>
                                    )}
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={() => toggleSuspend(u.id, u.name, u.suspended)} data-testid={`toggle-suspend-${u.id}`}>
                                      <ShieldOff className="h-4 w-4 mr-2 text-orange" /> {u.suspended ? 'Unsuspend' : 'Suspend'}
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handleApproveUser(u.id, false)} data-testid={`revoke-user-${u.id}`}>
                                      <XCircle className="h-4 w-4 mr-2 text-red-600" /> Revoke Approval
                                    </DropdownMenuItem>
                                  </>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'buyers' && (
          <div className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">Buyer Registrations ({users.filter(u => u.role === 'buyer').length})</h2>
              <button onClick={handleExportCSV} className="px-4 py-2 rounded-md bg-gold text-navy font-semibold text-sm" data-testid="export-buyers-csv-btn">
                <Download className="inline h-4 w-4 mr-2" /> Export CSV
              </button>
            </div>
            {users.filter(u => u.role === 'buyer').length === 0 ? <div className="text-center py-8 text-muted-foreground">No buyer registrations yet.</div> : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-3 px-4 font-medium">Name</th>
                      <th className="text-left py-3 px-4 font-medium">Email</th>
                      <th className="text-left py-3 px-4 font-medium">Status</th>
                      <th className="text-left py-3 px-4 font-medium">Joined</th>
                      <th className="text-left py-3 px-4 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.filter(u => u.role === 'buyer').map((u) => (
                      <tr key={u.id} className="border-b border-border hover:bg-muted/50" data-testid={`buyer-row-${u.id}`}>
                        <td className="py-3 px-4">{u.name}</td>
                        <td className="py-3 px-4">{u.email}</td>
                        <td className="py-3 px-4"><StatusBadge status={u.registration_status || (u.approved ? 'approved' : 'pending')} testid={`buyer-status-${u.id}`} /></td>
                        <td className="py-3 px-4 text-sm">{new Date(u.created_at).toLocaleDateString()}</td>
                        <td className="py-3 px-4">
                          {u.is_rejected_tombstone ? (
                            <button onClick={() => handleDeleteRejected(u.id, u.name || u.email)} className="text-red-600 hover:underline text-sm inline-flex items-center" data-testid={`delete-rejected-buyer-${u.id}`}>
                              <Trash2 className="h-4 w-4 inline mr-1" /> Delete Registration
                            </button>
                          ) : !u.approved ? (
                            <div className="flex space-x-2">
                              <button onClick={() => handleApproveUser(u.id, true)} className="text-green-600 hover:underline text-sm" data-testid={`buyer-approve-${u.id}`}><CheckCircle className="h-4 w-4 inline mr-1" /> Approve</button>
                              <button onClick={() => handleRejectUser(u.id, u.name || u.email)} className="text-red-600 hover:underline text-sm" data-testid={`buyer-reject-${u.id}`}><XCircle className="h-4 w-4 inline mr-1" /> Reject</button>
                            </div>
                          ) : <button onClick={() => handleApproveUser(u.id, false)} className="text-red-600 hover:underline text-sm" data-testid={`buyer-revoke-${u.id}`}>Revoke</button>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'suppliers' && (
          <div className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">Supplier Registrations ({users.filter(u => u.role === 'supplier').length})</h2>
              <button onClick={handleExportCSV} className="px-4 py-2 rounded-md bg-gold text-navy font-semibold text-sm" data-testid="export-suppliers-csv-btn">
                <Download className="inline h-4 w-4 mr-2" /> Export CSV
              </button>
            </div>
            {users.filter(u => u.role === 'supplier').length === 0 ? <div className="text-center py-8 text-muted-foreground">No supplier registrations yet.</div> : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-3 px-4 font-medium">Name</th>
                      <th className="text-left py-3 px-4 font-medium">Email</th>
                      <th className="text-left py-3 px-4 font-medium">Status</th>
                      <th className="text-left py-3 px-4 font-medium">Joined</th>
                      <th className="text-left py-3 px-4 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.filter(u => u.role === 'supplier').map((u) => (
                      <tr key={u.id} className="border-b border-border hover:bg-muted/50" data-testid={`supplier-row-${u.id}`}>
                        <td className="py-3 px-4">{u.name}</td>
                        <td className="py-3 px-4">{u.email}</td>
                        <td className="py-3 px-4"><StatusBadge status={u.registration_status || (u.approved ? 'approved' : 'pending')} testid={`supplier-status-${u.id}`} /></td>
                        <td className="py-3 px-4 text-sm">{new Date(u.created_at).toLocaleDateString()}</td>
                        <td className="py-3 px-4">
                          {u.is_rejected_tombstone ? (
                            <button onClick={() => handleDeleteRejected(u.id, u.name || u.email)} className="text-red-600 hover:underline text-sm inline-flex items-center" data-testid={`delete-rejected-supplier-${u.id}`}>
                              <Trash2 className="h-4 w-4 inline mr-1" /> Delete Registration
                            </button>
                          ) : !u.approved ? (
                            <div className="flex space-x-2">
                              <button onClick={() => handleApproveUser(u.id, true)} className="text-green-600 hover:underline text-sm" data-testid={`supplier-approve-${u.id}`}><CheckCircle className="h-4 w-4 inline mr-1" /> Approve</button>
                              <button onClick={() => handleRejectUser(u.id, u.name || u.email)} className="text-red-600 hover:underline text-sm" data-testid={`supplier-reject-${u.id}`}><XCircle className="h-4 w-4 inline mr-1" /> Reject</button>
                            </div>
                          ) : <button onClick={() => handleApproveUser(u.id, false)} className="text-red-600 hover:underline text-sm" data-testid={`supplier-revoke-${u.id}`}>Revoke</button>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'preregistrations' && (
          <div className="bg-card border border-border rounded-lg p-6">
            <h2 className="text-xl font-semibold mb-4">Pre-Registration Submissions ({inquiries.filter(i => i.source?.startsWith('pre_registration')).length})</h2>
            {inquiries.filter(i => i.source?.startsWith('pre_registration')).length === 0 ? <div className="text-center py-8 text-muted-foreground">No pre-registrations yet.</div> : (
              <div className="space-y-3">
                {inquiries.filter(i => i.source?.startsWith('pre_registration')).map((inq) => (
                  <div key={inq.id} className="p-4 rounded-lg border border-border" data-testid={`prereg-${inq.id}`}>
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <div className="font-semibold text-navy">{inq.name}</div>
                        <div className="text-sm text-muted-foreground flex flex-wrap items-center gap-x-3">
                          <span><Mail className="inline h-3 w-3 mr-1" />{inq.email}</span>
                          {inq.mobile && <span>📱 {inq.mobile}</span>}
                          {inq.organization && <span>🏢 {inq.organization}</span>}
                        </div>
                      </div>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${inq.source === 'pre_registration_buyer' ? 'bg-gold/20 text-navy' : 'bg-orange/20 text-orange'}`}>
                        {inq.source === 'pre_registration_buyer' ? 'BUYER' : 'SUPPLIER'}
                      </span>
                    </div>
                    <pre className="text-sm mb-2 whitespace-pre-wrap font-sans">{inq.message}</pre>
                    <div className="text-xs text-muted-foreground">{new Date(inq.created_at).toLocaleString()}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'tenders' && (
          <div className="bg-card border border-border rounded-lg p-6">
            <h2 className="text-xl font-semibold mb-4">All Tenders</h2>
            {tenders.length === 0 ? <div className="text-center py-8 text-muted-foreground">No tenders yet.</div> : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-3 px-4 font-medium">Title</th>
                      <th className="text-left py-3 px-4 font-medium">Value</th>
                      <th className="text-left py-3 px-4 font-medium">Status</th>
                      <th className="text-left py-3 px-4 font-medium">Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tenders.map((t) => (
                      <tr key={t.id} className="border-b border-border hover:bg-muted/50">
                        <td className="py-3 px-4">{t.title}</td>
                        <td className="py-3 px-4">₹{t.estimated_value?.toLocaleString()}</td>
                        <td className="py-3 px-4"><span className="px-2 py-1 rounded text-xs font-medium bg-navy/10 text-navy">{t.status?.replace(/_/g, ' ').toUpperCase()}</span></td>
                        <td className="py-3 px-4">{new Date(t.created_at).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'inquiries' && (
          <div className="bg-card border border-border rounded-lg p-6">
            <h2 className="text-xl font-semibold mb-4">Inquiries & Contact Submissions</h2>
            {inquiries.length === 0 ? <div className="text-center py-8 text-muted-foreground">No inquiries yet.</div> : (
              <div className="space-y-3">
                {inquiries.map((inq) => (
                  <div key={inq.id} className="p-4 rounded-lg border border-border" data-testid={`inquiry-${inq.id}`}>
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <div className="font-semibold text-navy">{inq.name}</div>
                        <div className="text-sm text-muted-foreground flex flex-wrap items-center gap-x-3">
                          <span><Mail className="inline h-3 w-3 mr-1" />{inq.email}</span>
                          {inq.mobile && <span>{inq.mobile}</span>}
                          {inq.organization && <span>{inq.organization}</span>}
                        </div>
                      </div>
                      <span className="px-2 py-1 rounded text-xs font-medium bg-orange/20 text-orange">{inq.source}</span>
                    </div>
                    <p className="text-sm mb-2">{inq.message}</p>
                    <div className="text-xs text-muted-foreground">{new Date(inq.created_at).toLocaleString()}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'manual_payments' && (
          <div className="bg-card border border-border rounded-lg p-6">
            <AdminManualPaymentsTab />
          </div>
        )}
      </div>

      {/* Grant Membership Modal */}
      {grantModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="grant-modal">
          <div className="bg-white rounded-lg max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-xl font-bold text-navy">Grant Membership</h3>
                <p className="text-sm text-muted-foreground">For: <strong>{grantModal.name}</strong> ({grantModal.email})</p>
              </div>
              <button onClick={() => setGrantModal(null)} className="text-muted-foreground hover:text-foreground" data-testid="grant-modal-close">
                <XCircle className="h-6 w-6" />
              </button>
            </div>
            <form onSubmit={submitGrant} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Plan</label>
                <select value={grantForm.plan_key} onChange={(e) => setGrantForm({ ...grantForm, plan_key: e.target.value })} className="w-full px-3 py-2 rounded border border-input" data-testid="grant-plan-select">
                  <option value="buyer_yearly">Buyer Yearly Subscription</option>
                  <option value="supplier_yearly">Supplier Yearly Subscription</option>
                </select>
              </div>

              <div className="flex items-center space-x-3">
                <label className="flex items-center text-sm">
                  <input type="checkbox" checked={grantForm.is_complimentary} onChange={(e) => setGrantForm({ ...grantForm, is_complimentary: e.target.checked, discount_percent: e.target.checked ? 0 : grantForm.discount_percent })} className="mr-2" data-testid="grant-complimentary-checkbox" />
                  Complimentary (100% free)
                </label>
              </div>

              {!grantForm.is_complimentary && (
                <div>
                  <label className="block text-sm font-medium mb-1">Discount % (0-100)</label>
                  <input type="number" min="0" max="100" value={grantForm.discount_percent} onChange={(e) => setGrantForm({ ...grantForm, discount_percent: Number(e.target.value) })} className="w-full px-3 py-2 rounded border border-input" data-testid="grant-discount-input" />
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Duration (days)</label>
                  <input type="number" min="1" value={grantForm.duration_days} onChange={(e) => setGrantForm({ ...grantForm, duration_days: Number(e.target.value) })} className="w-full px-3 py-2 rounded border border-input" data-testid="grant-duration-input" />
                  <p className="text-xs text-muted-foreground mt-1">Common: 30 / 90 / 180 / 365 / 730</p>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Or Custom Expiry Date</label>
                  <input type="date" value={grantForm.end_date_override} onChange={(e) => setGrantForm({ ...grantForm, end_date_override: e.target.value })} className="w-full px-3 py-2 rounded border border-input" data-testid="grant-enddate-input" />
                </div>
              </div>

              <div>
                <label className="flex items-center text-sm">
                  <input type="checkbox" checked={grantForm.mark_as_founding} onChange={(e) => setGrantForm({ ...grantForm, mark_as_founding: e.target.checked })} className="mr-2" data-testid="grant-founding-checkbox" />
                  Mark as Founding Member (auto 24 months)
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Internal Notes</label>
                <textarea value={grantForm.notes} onChange={(e) => setGrantForm({ ...grantForm, notes: e.target.value })} rows={2} className="w-full px-3 py-2 rounded border border-input" data-testid="grant-notes-input" />
              </div>

              <div className="flex justify-end space-x-2 pt-2">
                <button type="button" onClick={() => setGrantModal(null)} className="px-4 py-2 rounded border border-input text-sm">Cancel</button>
                <button type="submit" className="px-4 py-2 rounded bg-navy text-white text-sm font-semibold" data-testid="grant-submit-btn">Grant Membership</button>
              </div>
            </form>
          </div>
        </div>
      )}
      <ComplianceFooter compact className="mt-8 border-t border-border pt-4" />
    </div>
  );
};

const StatusBadge = ({ status, testid }) => {
  const s = (status || '').toLowerCase();
  if (s === 'approved') {
    return <span data-testid={testid} data-status="approved" className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 w-fit">Approved</span>;
  }
  if (s === 'rejected') {
    return <span data-testid={testid} data-status="rejected" className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700 w-fit">Rejected</span>;
  }
  return <span data-testid={testid} data-status="pending" className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 w-fit">Pending</span>;
};

export default AdminDashboard;
